"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "react-toastify";

import { useApi } from "@/hooks/useApi";
import { useWorkspace } from "@/providers/WorkspaceContext";
import {
  AdminOverrideRequest,
  AdminTokenAdjustmentRequest,
  PointsExchangeRequest,
  PointsToTokenExchange,
  TokenOverride,
  TokenPool,
  TokenTransaction,
  TokenUsage,
  TokenUsageStats
} from "@/lib/orchestrator/tokens/types";

// Constants
const CONSTANTS = {
  REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes
  BASE_PATH: "/api/tokens"
} as const;

// Context type definition
interface TokenContextType {
  // State
  tokenPool: TokenPool | null;
  tokenStats: TokenUsageStats | null;
  isLoading: boolean;
  error: Error | null;

  // Token pool operations
  getMyTokens: (workspaceId?: string) => Promise<any>;
  getTokenStatistics: (workspaceId?: string, timeRange?: number) => Promise<any>;

  // Admin operations
  addTokens: (poolId: string, data: AdminTokenAdjustmentRequest) => Promise<any>;
  removeTokens: (poolId: string, data: AdminTokenAdjustmentRequest) => Promise<any>;
  createOverride: (poolId: string, data: AdminOverrideRequest) => Promise<any>;
  getPoolOverrides: (poolId: string, activeOnly?: boolean) => Promise<any>;

  // Points exchange
  getExchangeHistory: (page?: number) => Promise<any>;
  exchangePointsForTokens: (data: PointsExchangeRequest) => Promise<any>;

  // Transaction operations
  getTransactions: (filters?: any) => Promise<any>;

  // Usage operations
  getUsageRecords: (filters?: any) => Promise<any>;
  getConversationUsage: (conversationId: string, page?: number) => Promise<any>;

  // Enhanced operations
  refreshTokenInfo: () => Promise<void>;
  resetTokenState: () => void;
}

interface TokenState {
  tokenPool: TokenPool | null;
  tokenStats: TokenUsageStats | null;
  transactions: TokenTransaction[];
  usageRecords: TokenUsage[];
  exchanges: PointsToTokenExchange[];
  overrides: TokenOverride[];
  isLoading: boolean;
  error: Error | null;
}

const initialState: TokenState = {
  tokenPool: null,
  tokenStats: null,
  transactions: [],
  usageRecords: [],
  exchanges: [],
  overrides: [],
  isLoading: false,
  error: null
};

// Create context
const TokenContext = createContext<TokenContextType | undefined>(undefined);

// Provider props
interface TokenProviderProps {
  children: React.ReactNode;
  autoRefresh?: boolean;
}

// Error class
class TokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TokenError";
  }
}

export function TokenProvider({
  children,
  autoRefresh = true
}: TokenProviderProps) {
  const { request } = useApi();
  const { currentWorkspace } = useWorkspace();
  const [state, setState] = useState<TokenState>(initialState);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const workspaceIdRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const lastRefreshTimeRef = useRef<number>(0);
  const refreshTokenInfoRef = useRef<() => Promise<void>>(async () => {});

  // Min time between refreshes (3 seconds)
  const REFRESH_DEBOUNCE = 3000;

  // Update state utility
  const updateState = useCallback((updates: Partial<TokenState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle errors
  const handleError = useCallback((error: Error, message: string) => {
    console.error(`[Token Error] ${message}:`, error);
    updateState({ error: new TokenError(message) });
    return null;
  }, [updateState]);

  // Track in-flight requests
  const pendingRequestsRef = useRef<{
    tokenPool: boolean;
    tokenStats: boolean;
  }>({
    tokenPool: false,
    tokenStats: false
  });

  // Get current user's token pool
  const getMyTokens = useCallback(async (workspaceId?: string) => {
    // Don't make a new request if one is already in flight
    if (pendingRequestsRef.current.tokenPool) {
      return null;
    }

    pendingRequestsRef.current.tokenPool = true;
    updateState({ isLoading: true });

    try {
      // Use the for_workspace endpoint with workspace_id query param
      const queryString = workspaceId ? `?workspace_id=${workspaceId}` : '';
      const response = await request(`${CONSTANTS.BASE_PATH}/pools/for_workspace/${queryString}`);

      if (response && response.data) {
        updateState({ tokenPool: response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error: any) {
      // Handle 404 errors gracefully - this means the user doesn't have a token pool yet
      if (error?.response?.status === 404) {
        console.info("User doesn't have a token pool yet");
        updateState({
          isLoading: false,
          // Clear existing token pool data to prevent stale data display
          tokenPool: null
        });
        return null;
      }

      updateState({ isLoading: false });
      return handleError(error as Error, "Failed to get token pool");
    } finally {
      pendingRequestsRef.current.tokenPool = false;
    }
  }, [request, updateState, handleError]);

  // Get token usage statistics
  const getTokenStatistics = useCallback(async (workspaceId?: string, timeRange: number = 30) => {
    // Don't make a new request if one is already in flight
    if (pendingRequestsRef.current.tokenStats) {
      return null;
    }

    pendingRequestsRef.current.tokenStats = true;
    updateState({ isLoading: true });

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (workspaceId) queryParams.append('workspace_id', workspaceId);
      queryParams.append('time_range', timeRange.toString());

      const response = await request(
        `${CONSTANTS.BASE_PATH}/pools/statistics/?${queryParams.toString()}`
      );

      if (response && response.data) {
        updateState({ tokenStats: response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error: any) {
      // Handle 404 errors gracefully - this means the user doesn't have token stats yet
      if (error?.response?.status === 404) {
        console.info("Token statistics not available yet");
        updateState({
          isLoading: false,
          // Clear existing token stats to prevent stale data display
          tokenStats: null
        });
        return null;
      }

      updateState({ isLoading: false });
      return handleError(error as Error, "Failed to get token statistics");
    } finally {
      pendingRequestsRef.current.tokenStats = false;
    }
  }, [request, updateState, handleError]);

  // Combined function to refresh token info with debounce
  const refreshTokenInfo = useCallback(async () => {
    const now = Date.now();

    // Skip if too soon or if either request is still in flight
    if ((now - lastRefreshTimeRef.current < REFRESH_DEBOUNCE && !isInitialLoadRef.current) ||
        pendingRequestsRef.current.tokenPool ||
        pendingRequestsRef.current.tokenStats) {
      return;
    }

    lastRefreshTimeRef.current = now;

    if (!currentWorkspace?.id) return;

    try {
      // Track request status to detect 404 and other error responses
      let hasFailedRequests = false;

      // Execute the API calls sequentially to avoid overloading the server
      const tokenPoolResponse = await getMyTokens(currentWorkspace.id);
      const tokenStatsResponse = await getTokenStatistics(currentWorkspace.id);

      // Check for null responses (indicates failure)
      if (!tokenPoolResponse) {
        console.warn('Token pool request returned null');
        hasFailedRequests = true;
      }

      if (!tokenStatsResponse) {
        console.warn('Token stats request returned null');
        hasFailedRequests = true;
      }

      // If both requests failed, we should prevent future automatic refreshes
      if (hasFailedRequests && refreshTimerRef.current) {
        console.warn('Multiple token requests failed - disabling automatic refresh');
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      isInitialLoadRef.current = false;
    } catch (error) {
      // Clear refresh timer on critical errors
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      handleError(error as Error, "Failed to refresh token information");
    }
  }, [currentWorkspace, getMyTokens, getTokenStatistics, handleError]);

  // Update the reference when refreshTokenInfo changes
  useEffect(() => {
    refreshTokenInfoRef.current = refreshTokenInfo;
  }, [refreshTokenInfo]);

  // Admin: Add tokens to a pool
  const addTokens = useCallback(async (poolId: string, data: AdminTokenAdjustmentRequest) => {
    try {
      const response = await request(`${CONSTANTS.BASE_PATH}/pools/${poolId}/add_tokens/`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (response && response.data) {
        // Update the token pool if it matches the current one
        if (state.tokenPool && state.tokenPool.id === poolId) {
          updateState({ tokenPool: response.data.pool });
        }

        toast.success(response.message || `Added ${data.amount} tokens successfully`);
        return response;
      }
      return null;
    } catch (error) {
      toast.error("Failed to add tokens");
      return handleError(error as Error, `Failed to add tokens to pool ${poolId}`);
    }
  }, [request, state.tokenPool, updateState, handleError]);

  // Admin: Remove tokens from a pool
  const removeTokens = useCallback(async (poolId: string, data: AdminTokenAdjustmentRequest) => {
    try {
      const response = await request(`${CONSTANTS.BASE_PATH}/pools/${poolId}/remove_tokens/`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (response && response.data) {
        // Update the token pool if it matches the current one
        if (state.tokenPool && state.tokenPool.id === poolId) {
          updateState({ tokenPool: response.data.pool });
        }

        toast.success(response.message || `Removed ${data.amount} tokens successfully`);
        return response;
      }
      return null;
    } catch (error) {
      toast.error("Failed to remove tokens");
      return handleError(error as Error, `Failed to remove tokens from pool ${poolId}`);
    }
  }, [request, state.tokenPool, updateState, handleError]);

  // Admin: Create an override for a token pool
  const createOverride = useCallback(async (poolId: string, data: AdminOverrideRequest) => {
    try {
      const response = await request(`${CONSTANTS.BASE_PATH}/pools/${poolId}/override/`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (response && response.data) {
        // Update the token pool if it matches the current one
        if (state.tokenPool && state.tokenPool.id === poolId) {
          updateState({ tokenPool: response.data.pool });
        }

        toast.success(response.message || `Override created successfully`);
        return response;
      }
      return null;
    } catch (error) {
      toast.error("Failed to create override");
      return handleError(error as Error, `Failed to create override for pool ${poolId}`);
    }
  }, [request, state.tokenPool, updateState, handleError]);

  // Get all overrides for a token pool
  const getPoolOverrides = useCallback(async (poolId: string, activeOnly: boolean = true) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('active_only', activeOnly ? 'true' : 'false');

      const response = await request(
        `${CONSTANTS.BASE_PATH}/pools/${poolId}/overrides/?${queryParams.toString()}`
      );

      if (response && response.data) {
        updateState({ overrides: response.data });
        return response;
      }
      return null;
    } catch (error) {
      return handleError(error as Error, `Failed to get overrides for pool ${poolId}`);
    }
  }, [request, updateState, handleError]);

  // Get token transactions with optional filters
  const getTransactions = useCallback(async (
    filters?: {
      transaction_type?: string;
      source_type?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
    }
  ) => {
    updateState({ isLoading: true });
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
      if (filters?.source_type) queryParams.append('source_type', filters.source_type);
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.page) queryParams.append('page', filters.page.toString());

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await request(`${CONSTANTS.BASE_PATH}/transactions/${queryString}`);

      if (response && response.data) {
        updateState({ transactions: response.data.data || response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error) {
      updateState({ isLoading: false });
      return handleError(error as Error, "Failed to get token transactions");
    }
  }, [request, updateState, handleError]);

  // Get token usage records with optional filters
  const getUsageRecords = useCallback(async (
    filters?: {
      usage_type?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
    }
  ) => {
    updateState({ isLoading: true });
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.usage_type) queryParams.append('usage_type', filters.usage_type);
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.page) queryParams.append('page', filters.page.toString());

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await request(`${CONSTANTS.BASE_PATH}/usage/${queryString}`);

      if (response && response.data) {
        updateState({ usageRecords: response.data.data || response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error) {
      updateState({ isLoading: false });
      return handleError(error as Error, "Failed to get token usage records");
    }
  }, [request, updateState, handleError]);

  // Get usage records for a specific conversation
  const getConversationUsage = useCallback(async (conversationId: string, page?: number) => {
    updateState({ isLoading: true });
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('conversation_id', conversationId);
      if (page) queryParams.append('page', page.toString());

      const response = await request(
        `${CONSTANTS.BASE_PATH}/usage/for_conversation/?${queryParams.toString()}`
      );

      if (response && response.data) {
        updateState({ usageRecords: response.data.data || response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error) {
      updateState({ isLoading: false });
      return handleError(error as Error, `Failed to get usage for conversation ${conversationId}`);
    }
  }, [request, updateState, handleError]);

  // Get points exchange history
  const getExchangeHistory = useCallback(async (page?: number) => {
    updateState({ isLoading: true });
    try {
      const queryString = page ? `?page=${page}` : '';
      const response = await request(`${CONSTANTS.BASE_PATH}/points-exchange/${queryString}`);

      if (response && response.data) {
        updateState({ exchanges: response.data.data || response.data, isLoading: false });
        return response;
      }

      updateState({ isLoading: false });
      return null;
    } catch (error) {
      updateState({ isLoading: false });
      return handleError(error as Error, "Failed to get points exchange history");
    }
  }, [request, updateState, handleError]);

  // Exchange points for tokens
  const exchangePointsForTokens = useCallback(async (data: PointsExchangeRequest & { workspace_id: string }) => {
    try {
      const response = await request(`${CONSTANTS.BASE_PATH}/points-exchange/`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (response) {
        // Refresh token info after exchange using the ref to avoid circular dependency
        if (refreshTokenInfoRef.current) {
          refreshTokenInfoRef.current();
        }

        toast.success(response.message || "Points exchanged for tokens successfully");
        return response;
      }
      return null;
    } catch (error) {
      toast.error("Failed to exchange points");
      return handleError(error as Error, "Failed to exchange points for tokens");
    }
  }, [request, handleError]);

  // Set up auto-refresh timer
  useEffect(() => {
    if (!autoRefresh || !currentWorkspace?.id) return;

    // Clear existing timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    // Initial load - only if we have a workspace
    refreshTokenInfo();

    // Set up timer for periodic refresh
    refreshTimerRef.current = setInterval(() => {
      if (refreshTokenInfoRef.current) {
        refreshTokenInfoRef.current();
      }
    }, CONSTANTS.REFRESH_INTERVAL);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, currentWorkspace, refreshTokenInfo]);

  // Reset token state function
  const resetTokenState = useCallback(() => {
    setState(initialState);
    isInitialLoadRef.current = true;
    workspaceIdRef.current = null;

    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Reset state when workspace changes
  useEffect(() => {
    if (currentWorkspace?.id !== workspaceIdRef.current) {
      // Reset state when workspace changes
      resetTokenState();
      workspaceIdRef.current = currentWorkspace?.id || null;

      // Load token data for new workspace
      if (currentWorkspace?.id) {
        refreshTokenInfo();

        // Set up a new refresh timer
        if (autoRefresh && !refreshTimerRef.current) {
          refreshTimerRef.current = setInterval(() => {
            if (refreshTokenInfoRef.current) {
              refreshTokenInfoRef.current();
            }
          }, CONSTANTS.REFRESH_INTERVAL);
        }
      }
    }
  }, [currentWorkspace, refreshTokenInfo, resetTokenState, autoRefresh]);

  // Create context value
  const contextValue = useMemo<TokenContextType>(() => ({
    // State
    tokenPool: state.tokenPool,
    tokenStats: state.tokenStats,
    isLoading: state.isLoading,
    error: state.error,

    // Token pool operations
    getMyTokens,
    getTokenStatistics,

    // Admin operations
    addTokens,
    removeTokens,
    createOverride,
    getPoolOverrides,

    // Points exchange
    getExchangeHistory,
    exchangePointsForTokens,

    // Transaction operations
    getTransactions,

    // Usage operations
    getUsageRecords,
    getConversationUsage,

    // Enhanced operations
    refreshTokenInfo,
    resetTokenState
  }), [
    state,
    getMyTokens,
    getTokenStatistics,
    addTokens,
    removeTokens,
    createOverride,
    getPoolOverrides,
    getExchangeHistory,
    exchangePointsForTokens,
    getTransactions,
    getUsageRecords,
    getConversationUsage,
    refreshTokenInfo,
    resetTokenState
  ]);

  return (
    <TokenContext.Provider value={contextValue}>
      {children}
    </TokenContext.Provider>
  );
}

// Custom hook to use the token context
export function useToken(): TokenContextType {
  const context = useContext(TokenContext);

  if (!context) {
    throw new Error(
      "useToken must be used within a TokenProvider. " +
      "Please ensure the component is wrapped in a TokenProvider."
    );
  }

  return context;
}